// The thin fetch wrapper around the generated contract (openapi-fetch over src/api/schema.d.ts).
// Adds the bearer token, retries once with a renewed token on 401, and turns every non-2xx answer into an
// ApiProblem that keeps the API's sentence (`detail`) verbatim.
import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';
import type { Problem, ProblemCode } from './types';
import type { TokenSource } from '../auth/oidc';

export class ApiProblem extends Error {
  readonly status: number;
  readonly code: ProblemCode | 'NetworkError' | 'UnexpectedResponse';
  readonly detail: string;
  readonly title: string;
  readonly reason?: string;

  constructor(status: number, p: Partial<Omit<Problem, 'code'>> & { code: ApiProblem['code'] }) {
    super(p.detail ?? p.title ?? `HTTP ${status}`);
    this.status = status;
    this.code = p.code;
    this.title = p.title ?? `HTTP ${status}`;
    this.detail = p.detail ?? p.title ?? `The API answered HTTP ${status}.`;
    this.reason = p.reason;
  }
}

export async function toProblem(res: Response): Promise<ApiProblem> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as Partial<Problem>;
    if (body && typeof body === 'object' && typeof body.code === 'string') {
      return new ApiProblem(res.status, body as Problem);
    }
  } catch {
    /* not JSON */
  }
  return new ApiProblem(res.status, {
    code: 'UnexpectedResponse',
    title: `HTTP ${res.status}`,
    detail: `The API answered HTTP ${res.status} ${res.statusText}${text ? `: ${text.slice(0, 300)}` : ''}.`,
  });
}

export function createApi(baseUrl: string, auth: TokenSource, fetchImpl: typeof fetch = (...a) => fetch(...a)) {
  // 401 → renew once and replay. Done in a fetch wrapper so the request body can be replayed.
  const authedFetch: typeof fetch = async (input, init) => {
    const req = new Request(input as RequestInfo, init);
    const replay = req.clone();
    req.headers.set('Authorization', `Bearer ${await auth.token()}`);
    let res: Response;
    try {
      res = await fetchImpl(req);
    } catch (e) {
      throw new ApiProblem(0, { code: 'NetworkError', title: 'Network error', detail: `The API could not be reached (${(e as Error).message}).` });
    }
    if (res.status !== 401) return res;
    replay.headers.set('Authorization', `Bearer ${await auth.renew()}`);
    return fetchImpl(replay);
  };

  const client = createClient<paths>({ baseUrl, fetch: authedFetch });
  const problems: Middleware = {
    async onResponse({ response }) {
      if (!response.ok) throw await toProblem(response);
      return undefined;
    },
  };
  client.use(problems);
  return client;
}

export type Api = ReturnType<typeof createApi>;

/** Unwraps an openapi-fetch result; errors were already thrown by the middleware. */
export function must<T>(r: { data?: T; error?: unknown; response: Response }): T {
  if (r.data === undefined) throw new ApiProblem(r.response.status, { code: 'UnexpectedResponse', detail: `The API answered HTTP ${r.response.status} without a body.` });
  return r.data;
}
